import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const AdminGalleryManagement = () => {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general'
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category: '',
    isActive: true
  });

  const fileInputRef = useRef(null);

  const requestGallery = async (url, options = {}) => {
    const response = await fetch(url, { ...options, credentials: 'include' });
    if (response.status === 401) {
      navigate('/admin?returnTo=%2Fadmin%2Fgallery-management&reason=session-expired', { replace: true });
      return null;
    }
    return response;
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'events', label: 'Events' },
    { value: 'sports', label: 'Sports' },
    { value: 'academic', label: 'Academic' },
    { value: 'celebrations', label: 'Celebrations' },
    { value: 'facilities', label: 'Facilities' }
  ];

  useEffect(() => {
    loadGallery();
    loadStats();
  }, []);

  const loadGallery = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GALLERY);
      if (response.ok) {
        const data = await response.json();
        setGallery(data);
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      alert('Failed to load gallery media');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await requestGallery(API_ENDPOINTS.GALLERY.replace('/media', '/stats'));
      if (!response) return;
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/avi', 'video/mov'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only images and videos are allowed.');
      return;
    }

    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size too large. Maximum size is 50MB.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('media', file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);

      const response = await requestGallery(`${API_ENDPOINTS.GALLERY.replace('/media', '/upload')}`, {
        method: 'POST',
        body: formData
      });
      if (!response) return;

      if (response.ok) {
        alert('Media uploaded successfully!');
        setShowUploadModal(false);
        setUploadForm({ title: '', description: '', category: 'general' });
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadGallery();
        loadStats();
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleEditMedia = async () => {
    if (!editingMedia) return;

    try {
      const response = await requestGallery(`${API_ENDPOINTS.GALLERY}/${editingMedia.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      if (!response) return;

      if (response.ok) {
        alert('Media updated successfully!');
        setShowEditModal(false);
        setEditingMedia(null);
        loadGallery();
      } else {
        alert('Failed to update media');
      }
    } catch (error) {
      console.error('Error updating media:', error);
      alert('Failed to update media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this mediaLoading This action cannot be undone.')) {
      return;
    }

    try {
      const response = await requestGallery(`${API_ENDPOINTS.GALLERY}/${mediaId}`, {
        method: 'DELETE'
      });
      if (!response) return;

      if (response.ok) {
        alert('Media deleted successfully!');
        loadGallery();
        loadStats();
      } else {
        alert('Failed to delete media');
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media');
    }
  };

  const openEditModal = (media) => {
    setEditingMedia(media);
    setEditForm({
      title: media.title,
      description: media.description,
      category: media.category,
      isActive: media.isActive
    });
    setShowEditModal(true);
  };

  const filteredGallery = selectedCategory === 'all'
    ? gallery
    : gallery.filter(item => item.category === selectedCategory);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="text-center">Loading gallery management...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-blue-900">Gallery & Social Media Management</h1>
          <p className="text-gray-600 mt-2">Manage school gallery media and monitor social engagement</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition duration-300"
          >
            Upload Media
          </button>
          <DateTimeDisplay />
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-blue-600">{stats.totalMedia}</div>
            <div className="text-gray-600">Total Media</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-green-600">{stats.totalLikes}</div>
            <div className="text-gray-600">Total Likes</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-purple-600">{stats.totalComments}</div>
            <div className="text-gray-600">Total Comments</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-orange-600">{stats.totalViews}</div>
            <div className="text-gray-600">Total Views</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <label className="font-medium text-gray-700">Filter by Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredGallery.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="relative">
              {item.fileType === 'image' ? (
                <img
                  src={getImageUrl(item.filePath)}
                  alt={item.title}
                  className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition duration-300"
                  onClick={() => setSelectedMedia(item)}
                />
              ) : (
                <video
                  src={getImageUrl(item.filePath)}
                  className="w-full h-48 object-cover cursor-pointer"
                  onClick={() => setSelectedMedia(item)}
                  muted
                />
              )}

              <div className="absolute top-2 right-2 flex space-x-2">
                <button
                  onClick={() => openEditModal(item)}
                  className="bg-blue-600 text-white p-1 rounded hover:bg-blue-700 transition duration-300"
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDeleteMedia(item.id)}
                  className="bg-red-600 text-white p-1 rounded hover:bg-red-700 transition duration-300"
                  title="Delete"
                >
                  Delete
                </button>
              </div>

              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                {item.fileType === 'video' ? 'Video' : '📷'} {item.category}
              </div>
            </div>

            <div className="p-4">
              <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <span>Like {item.likes}</span>
                  <span>💬 {item.comments.length}</span>
                  <span>👁️ {item.views}</span>
                </div>
                <div className="text-xs">
                  {new Date(item.uploadDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredGallery.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No media found in this category.</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Upload Gallery Media</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter media title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter media description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Media File
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={uploading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: JPG, PNG, GIF, WebP, MP4, WebM, AVI, MOV (Max: 50MB)
                  </p>
                </div>

                {uploading && (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="text-sm text-gray-600 mt-2">Uploading...</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadForm({ title: '', description: '', category: 'general' });
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Edit Gallery Media</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active (visible on gallery page)
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditMedia}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">{selectedMedia.title}</h3>
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-4">
                {selectedMedia.fileType === 'image' ? (
                  <img
                    src={getImageUrl(selectedMedia.filePath)}
                    alt={selectedMedia.title}
                    className="w-full max-h-96 object-contain rounded"
                  />
                ) : (
                  <video
                    src={getImageUrl(selectedMedia.filePath)}
                    controls
                    className="w-full max-h-96 rounded"
                  />
                )}
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Description:</strong> {selectedMedia.description}</p>
                <p><strong>Category:</strong> {categories.find(c => c.value === selectedMedia.category)?.label}</p>
                <p><strong>Uploaded by:</strong> {selectedMedia.uploadedBy}</p>
                <p><strong>Upload date:</strong> {new Date(selectedMedia.uploadDate).toLocaleString()}</p>
                <p><strong>File size:</strong> {formatFileSize(selectedMedia.size)}</p>
                <div className="flex space-x-4 mt-2">
                  <span>Like {selectedMedia.likes} likes</span>
                  <span>💬 {selectedMedia.comments.length} comments</span>
                  <span>👁️ {selectedMedia.views} views</span>
                </div>
              </div>

              {/* Comments Section */}
              <div className="mt-6">
                <h4 className="font-bold text-gray-800 mb-3">Comments ({selectedMedia.comments.length})</h4>
                <div className="space-y-3 max-h-40 overflow-y-auto">
                  {selectedMedia.comments.length === 0 ? (
                    <p className="text-gray-500 text-sm">No comments yet.</p>
                  ) : (
                    selectedMedia.comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-800">{comment.userName}</p>
                            <p className="text-gray-600 text-sm">{comment.comment}</p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGalleryManagement;



